import { ButtonBase, Tooltip } from '@mui/material';
import { Star } from 'lucide-react';
import { useIntl } from 'react-intl';
import type { PrimaryDatasetKind } from '~/backend/connector-next-api/primaryDatasetClient';

/** What the star says about this dataset's designation. */
export type PrimaryState =
    /** This dataset holds the designation. */
    | 'primary'
    /** No dataset of this kind holds it. */
    | 'unset'
    /** Another dataset of this kind holds it. */
    | 'other';

type PrimaryStarProps = {
    kind: PrimaryDatasetKind;
    state: PrimaryState;
    /** The dataset's display name, for the control's accessible name. */
    name: string;
    /** Designates this dataset, or clears the designation when it holds it. */
    onToggle: () => void;
    disabled?: boolean;
};

const LABEL_ID: Record<PrimaryDatasetKind, Record<PrimaryState, string>> = {
    image: {
        primary: 'patientList.primary.image.clear',
        unset: 'patientList.primary.image.set',
        other: 'patientList.primary.image.set',
    },
    structureSet: {
        primary: 'patientList.primary.structureSet.clear',
        unset: 'patientList.primary.structureSet.set',
        other: 'patientList.primary.structureSet.set',
    },
};

/**
 * The primary designation of one dataset: filled where this dataset holds it,
 * a visible outline where the patient has no primary of its kind at all, and
 * barely there where another dataset holds it.
 *
 * The two unfilled states are deliberately different weights, and this way
 * round. "Nobody has chosen" is the one that wants answering, so it is the one
 * that shows; "another row holds it" is settled, and on a long list every row
 * but one would otherwise carry a mark competing with the dataset's name. The
 * primary itself is the only thing that should draw the eye.
 *
 * Clicking the held designation clears it. Cleared is a real state — the open
 * falls back to its own defaults — not a way of refusing the click.
 */
export default function PrimaryStar({
    kind,
    state,
    name,
    onToggle,
    disabled = false,
}: PrimaryStarProps) {
    const intl = useIntl();
    const label = intl.formatMessage({ id: LABEL_ID[kind][state] }, { name });
    const isPrimary = state === 'primary';

    return (
        <Tooltip title={label}>
            {/* The button is the hit area and carries no paint; the glyph
                inside is the paint. Styling the button directly would draw
                its focus ring around the padding rather than the star. */}
            <ButtonBase
                data-primary-kind={kind}
                data-primary-state={state}
                disabled={disabled}
                aria-pressed={isPrimary}
                aria-label={label}
                onClick={onToggle}
                sx={{
                    width: 20,
                    height: 20,
                    flexShrink: 0,
                    borderRadius: '2px',
                    color: isPrimary
                        ? 'warning.main'
                        : state === 'unset'
                          ? 'text.disabled'
                          : 'divider',
                    '&:hover': { color: 'warning.light' },
                    '&.Mui-disabled': { color: 'action.disabled' },
                }}
            >
                <Star
                    size={13}
                    aria-hidden
                    fill={isPrimary ? 'currentColor' : 'none'}
                />
            </ButtonBase>
        </Tooltip>
    );
}
